import { MailCheck, MailWarning, MailX } from 'lucide-react'

import type { EmailDeliveryStatus, InvoiceStatus } from '@/shared/domain'
import { Badge } from '@/shared/ui/primitives'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/primitives/tooltip'

export type InvoiceStatusBadgeProps = {
  status: InvoiceStatus
  emailDeliveryStatus?: EmailDeliveryStatus
}

type BadgeMeta = {
  label: string
  variant: 'default' | 'secondary' | 'outline' | 'destructive' | 'info' | 'warning'
}

const STATUS_META: Record<InvoiceStatus, BadgeMeta> = {
  draft: { label: 'Draft', variant: 'outline' },
  sent: { label: 'Sent', variant: 'info' },
  paid: { label: 'Paid', variant: 'default' },
  partially_paid: { label: 'Partial', variant: 'warning' },
  void: { label: 'Void', variant: 'outline' },
  uncollectible: { label: 'Uncollectible', variant: 'outline' },
  refunded: { label: 'Refunded', variant: 'outline' },
  failed: { label: 'Failed', variant: 'destructive' },
  scheduled: { label: 'Scheduled', variant: 'secondary' },
}

type DeliveryMeta = {
  icon: typeof MailCheck
  tooltip: string
  label: string
}

const DELIVERY_META: Record<
  Exclude<EmailDeliveryStatus, 'not_attempted'>,
  DeliveryMeta
> = {
  delivered: {
    icon: MailCheck,
    tooltip: 'Email delivered',
    label: 'Email delivered',
  },
  skipped: {
    icon: MailWarning,
    tooltip: 'Email skipped',
    label: 'Email skipped',
  },
  failed: {
    icon: MailX,
    tooltip: 'Email delivery failed',
    label: 'Email delivery failed',
  },
}

export function InvoiceStatusBadge({
  status,
  emailDeliveryStatus,
}: InvoiceStatusBadgeProps) {
  const meta = STATUS_META[status]
  const delivery =
    emailDeliveryStatus && emailDeliveryStatus !== 'not_attempted'
      ? DELIVERY_META[emailDeliveryStatus]
      : null

  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={meta.variant}>{meta.label}</Badge>
      {delivery ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center text-muted-foreground"
                aria-label={delivery.label}
              >
                <delivery.icon className="size-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{delivery.tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </span>
  )
}
